import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaChevronLeft, FaChevronRight, FaStar } from "react-icons/fa";
import Images from "../../utils/Images";

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      rating: 5,
      text: "TripSecure has transformed our insurance operations. The platform is intuitive, reliable, and has helped us reach thousands of new customers.",
      provider: "Sunu",
      logo: Images.sunu  
    },
    {
      id: 2,
      rating: 5,
      text: "The integration was seamless and the support team is exceptional. We've seen a significant increase in our travel insurance portfolio.",
      provider: "sunu",
      logo: Images.sunu
    },
    {
      id: 3,
      rating: 5,
      text: "Outstanding platform that delivers on its promises. The real-time analytics and automated processes have revolutionized our operations.",
      provider: "sunu",
      logo: Images.sunu
    },
    {
      id: 4,
      rating: 5,
      text: "TripSecure's innovative approach has given us a competitive edge. The platform is robust, scalable, and perfectly suited for our needs.",
      provider: "sunu",
      logo: Images.sunu
    }
  ];

  const scrollToCard = (index) => {
    if (carouselRef.current) {
      const cardWidth = 320;
      const gap = 24;
      const scrollPosition = index * (cardWidth + gap);
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  const scrollNext = () => {
    const nextIndex = Math.min(currentIndex + 1, testimonials.length - 1);
    scrollToCard(nextIndex);
  };

  const scrollPrev = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    scrollToCard(prevIndex);
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const cardWidth = 320;
      const gap = 24;
      const newIndex = Math.round(scrollLeft / (cardWidth + gap));
      setCurrentIndex(newIndex);
    }
  };

  return (
    <section className="overflow-x-hidden bg-white">
      {/* Testimonials Section */}
      <div className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl">
              Provider{" "}
              <span className="relative">
                Testimonials
                <div className="absolute right-0 bottom-0 left-0 h-2 bg-green-200 rounded-full"></div>
              </span>
            </h2>
            <p className="text-lg text-gray-600">
              See What Our Providers Say About Us
            </p>
          </motion.div>

          {/* Carousel */}
          <div className="relative overflow-hidden">
            {/* Navigation Arrows */}
            <button
              onClick={scrollPrev}
              disabled={currentIndex === 0}
              className="flex absolute left-2 top-1/2 z-10 justify-center items-center w-12 h-12 text-gray-600 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-300 -translate-y-1/2 hover:text-green-600 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronLeft size={20} />
            </button>

            <button
              onClick={scrollNext}
              disabled={currentIndex === testimonials.length - 1}
              className="flex absolute right-2 top-1/2 z-10 justify-center items-center w-12 h-12 text-gray-600 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-300 -translate-y-1/2 hover:text-green-600 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronRight size={20} />
            </button>

            {/* Cards Container */}
            <div 
              ref={carouselRef}
              onScroll={handleScroll}
              className="flex overflow-x-auto gap-6 pb-8 scrollbar-hide scroll-smooth"
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex-shrink-0 w-80 lg:w-96"
                >
                  {/* Testimonial Card */}
                  <div className="p-8 h-full bg-white rounded-2xl border border-gray-100 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                    {/* Rating Stars */}
                    <div className="flex mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <FaStar key={i} size={20} className="text-green-500" />
                      ))}
                    </div>

                    {/* Text */}
                    <p className="mb-6 text-sm leading-relaxed text-gray-700">
                      "{testimonial.text}"
                    </p>

                    {/* Provider */}
                    <div className="flex items-center space-x-3">
                      <img
                        src={testimonial.logo}
                        alt={`${testimonial.provider} logo`}
                        className="object-contain w-40 h-40"
                      />
                      {/* <span className="font-medium text-gray-900">
                        {testimonial.provider}
                      </span> */}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Indicators */}
            <div className="flex justify-center mt-8">
              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToCard(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                      index === currentIndex ? "bg-green-500" : "bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Footer */}
      <div className="px-4 py-16 bg-green-500 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="mb-2 text-3xl font-bold text-white md:text-4xl lg:text-3xl">
                100,000+
              </div>
              <div className="text-sm text-white md:text-base">
                Policies issued
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="mb-2 text-xl font-bold text-white md:text-xl lg:text-3xl">
                30+
              </div>
              <div className="text-sm text-white md:text-base">
                registered insurance providers
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <div className="mb-2 text-xl font-bold text-white md:text-xl lg:text-3xl">  
                GHS 1 million+
              </div>
              <div className="text-sm text-white md:text-base">
                in claims paid
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center"
            >
              <div className="mb-2 text-xl font-bold text-white md:text-xl lg:text-3xl">
                Active in 10+
              </div>
              <div className="text-sm text-white md:text-base">
                regions of Ghana
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scroll-smooth {
          scroll-behavior: smooth;
        }
      `}</style>
    </section>
  );
}
